import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.96
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
interface ProjectCardProps {
  project: (typeof projects)[0];
  className?: string;
  index: number;
}
function ProjectCard({
  project,
  className = '',
  index
}: ProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const {
    t
  } = useLanguage();
  return <motion.article variants={itemVariants} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className={`group relative overflow-hidden rounded-2xl bg-brand-dark cursor-pointer ${className}`} data-id="element-283">
      {/* Image */}
      <motion.img src={project.src} alt={project.title} className="absolute inset-0 w-full h-full object-cover" animate={{
      scale: isHovered ? 1.08 : 1
    }} transition={{
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-284" />

      {/* Persistent bottom gradient for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" data-id="element-285" />

      {/* Gold accent corner */}
      <div className="absolute top-4 left-4 z-10" data-id="element-286">
        <motion.div animate={{
        opacity: isHovered ? 1 : 0.8,
        scale: isHovered ? 1.05 : 1
      }} transition={{
        duration: 0.3
      }} className="flex items-center gap-1.5 bg-brand-gold/95 text-brand-black px-3 py-1.5 rounded-full text-xs font-bold tracking-wide shadow-lg backdrop-blur-sm" data-id="element-287">
          <Sparkles className="w-3 h-3" strokeWidth={2.5} data-id="element-288" />
          <span data-id="element-289">{project.tag}</span>
        </motion.div>
      </div>

      {/* Project number */}
      <div className="absolute top-4 right-4 z-10" data-id="element-290">
        <span className="font-serif text-white/40 text-sm tracking-widest" data-id="element-291">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      {/* Bottom content */}
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 z-10" data-id="element-292">
        <motion.div animate={{
        y: isHovered ? 0 : 8,
        opacity: 1
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} data-id="element-293">
          <h3 className="font-serif text-white text-lg md:text-xl font-bold leading-tight mb-2 text-balance" data-id="element-294">
            {project.title}
          </h3>
          <motion.div animate={{
          opacity: isHovered ? 1 : 0,
          y: isHovered ? 0 : 10
        }} transition={{
          duration: 0.3
        }} className="flex items-center gap-2 text-brand-gold text-sm font-semibold" data-id="element-295">
            <span data-id="element-296">{t('portfolio.completed')}</span>
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} data-id="element-297" />
          </motion.div>
        </motion.div>
      </div>

      {/* Gold border on hover */}
      <motion.div animate={{
      opacity: isHovered ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="absolute inset-0 rounded-2xl ring-2 ring-brand-gold pointer-events-none" data-id="element-298" />
    </motion.article>;
}
export function PortfolioSection() {
  const {
    t
  } = useLanguage();
  const projects = [{
    src: 'https://cdn.magicpatterns.com/uploads/f8SvMDgeL72qsNxaLnScTV/626315162_18447191575100071_3729629091570212903_n.jpg',
    title: t('portfolio.p1Title'),
    tag: t('portfolio.p1Tag')
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/kPQuEsT2zjXpehXeFoXFCd/637055771_18451457725100071_9064146037266162525_n.jpg',
    title: t('portfolio.p2Title'),
    tag: t('portfolio.p2Tag')
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/3sK4dxv2aWM4g7eBG92hV6/639765700_18452224459100071_5383018511946973699_n.jpg',
    title: t('portfolio.p3Title'),
    tag: t('portfolio.p3Tag')
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/xiDmnPHzYosQbr5vhhzor7/657736660_18459427129100071_7060071746529659010_n.jpg',
    title: t('portfolio.p4Title'),
    tag: t('portfolio.p4Tag')
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/xmVtMJ8KYP6cX3Q9jCZRT8/670758186_18462916690100071_2136875187969727028_n.jpg',
    title: t('portfolio.p5Title'),
    tag: t('portfolio.p5Tag')
  }];
  return <section id="trabajos" className="py-20 lg:py-28 bg-brand-cream relative overflow-hidden" data-id="element-299">
      {/* Subtle background accent */}
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-brand-gold/5 rounded-full blur-[120px] pointer-events-none" data-id="element-300" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-301">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-302">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-black text-brand-gold text-sm font-bold tracking-widest uppercase mb-6" data-id="element-303">
            <Sparkles className="w-4 h-4" data-id="element-304" />
            <span data-id="element-305">{t('portfolio.badge')}</span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-5xl font-serif font-bold text-brand-black mb-6 leading-tight text-balance" data-id="element-306">
            {t('portfolio.title')}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-lg text-brand-text/70" data-id="element-307">
            {t('portfolio.desc')}
          </motion.p>
        </div>

        {/* Bento grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5" data-id="element-308">
          {/* Row 1: Featured large + tall */}
          <ProjectCard project={projects[0]} index={0} className="md:col-span-4 aspect-[16/10] md:aspect-auto md:row-span-2 md:h-[640px]" data-id="element-309" />
          <ProjectCard project={projects[1]} index={1} className="md:col-span-2 aspect-square md:aspect-auto md:h-[310px]" data-id="element-310" />
          <ProjectCard project={projects[2]} index={2} className="md:col-span-2 aspect-square md:aspect-auto md:h-[310px]" data-id="element-311" />

          {/* Row 2 */}
          <ProjectCard project={projects[3]} index={3} className="md:col-span-3 aspect-[4/3] md:h-[360px]" data-id="element-312" />
          <ProjectCard project={projects[4]} index={4} className="md:col-span-3 aspect-[4/3] md:h-[360px]" data-id="element-313" />
        </motion.div>

        {/* Stat strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 bg-brand-black rounded-2xl p-8 md:p-10" data-id="element-314">
          {[{
          num: t('portfolio.s1Num'),
          label: t('portfolio.s1Label')
        }, {
          num: t('portfolio.s2Num'),
          label: t('portfolio.s2Label')
        }, {
          num: t('portfolio.s3Num'),
          label: t('portfolio.s3Label')
        }, {
          num: t('portfolio.s4Num'),
          label: t('portfolio.s4Label')
        }].map((stat, i) => <div key={i} className="text-center border-l border-white/10 first:border-l-0 md:[&:nth-child(3)]:border-l md:[&:nth-child(3)]:border-white/10" data-id="element-315">
              <div className="font-serif text-3xl md:text-5xl font-bold text-brand-gold mb-2" data-id="element-316">
                {stat.num}
              </div>
              <div className="text-xs md:text-sm text-white/60 uppercase tracking-widest" data-id="element-317">
                {stat.label}
              </div>
            </div>)}
        </motion.div>
      </div>
    </section>;
}